import { NavLink, Outlet } from 'react-router';

import { PERMISSIONS, hasPermission, type Permission } from '@/core/auth/permissions.js';
import { useLogout, useSession } from '@/features/auth';
import { Icon, cx, type IconName } from '@/shared/ui/index.js';

import styles from './app-layout.module.css';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: IconName;
  readonly permission: Permission;
}

interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

/**
 * Una sola fuente para la navegación; el permiso decide qué se muestra.
 *
 * Agrupada por lo que la persona viene a hacer: ocho enlaces sueltos obligan a
 * leerlos todos cada vez, tres bloques cortos no.
 */
const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Operación',
    items: [
      { to: '/', label: 'Inicio', icon: 'inicio', permission: PERMISSIONS.reportsRead },
      { to: '/insumos', label: 'Insumos', icon: 'insumos', permission: PERMISSIONS.productsRead },
      { to: '/inventario', label: 'Stock', icon: 'stock', permission: PERMISSIONS.productsRead },
      {
        to: '/menu-del-dia',
        label: 'Menú del día',
        icon: 'menu',
        permission: PERMISSIONS.inventoryWrite,
      },
    ],
  },
  {
    label: 'Análisis',
    items: [
      { to: '/kardex', label: 'Kardex', icon: 'kardex', permission: PERMISSIONS.reportsRead },
      {
        to: '/historial',
        label: 'Historial',
        icon: 'puntos',
        permission: PERMISSIONS.reportsRead,
      },
      { to: '/alertas', label: 'Alertas', icon: 'alertas', permission: PERMISSIONS.reportsRead },
      { to: '/reportes', label: 'Reportes', icon: 'reportes', permission: PERMISSIONS.reportsRead },
    ],
  },
  {
    label: 'Administración',
    items: [
      { to: '/usuarios', label: 'Usuarios', icon: 'usuarios', permission: PERMISSIONS.usersManage },
    ],
  },
];

const ROLES: Readonly<Record<string, string>> = {
  ADMIN: 'Administrador',
  GENERAL_MANAGER: 'Gerencia',
  KITCHEN_MANAGER: 'Cocina',
};

export function AppLayout() {
  const { user } = useSession();
  const logout = useLogout();
  const grupos = NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.map((item) => ({
      ...item,
      permitido: hasPermission(user?.permissions, item.permission),
    })),
  }));

  return (
    <div className={styles.shell}>
      <a className={styles.skip} href="#contenido">
        Saltar al contenido
      </a>

      <header className={styles.sidebar}>
        <div className={styles.brand}>
          <span aria-hidden="true" className={styles.brandMark}>
            <Icon height={20} name="menu" width={20} />
          </span>
          <span className={styles.brandText}>
            <span className={styles.brandName}>Inventario</span>
            <span className={styles.brandSub}>Cocina PECEPE</span>
          </span>
        </div>

        <nav aria-label="Secciones" className={styles.nav}>
          {grupos.map((group) => (
            <div className={styles.group} key={group.label}>
              <p className={styles.groupLabel}>{group.label}</p>
              {group.items.map((item) =>
                item.permitido ? (
                  <NavLink
                    className={({ isActive }) => cx(styles.link, isActive && styles.active)}
                    end={item.to === '/'}
                    key={item.to}
                    to={item.to}
                  >
                    <span aria-hidden="true" className={styles.linkIcon}>
                      <Icon name={item.icon} />
                    </span>
                    {item.label}
                  </NavLink>
                ) : (
                  <span
                    aria-disabled="true"
                    className={cx(styles.link, styles.linkLocked)}
                    key={item.to}
                    title="Tu rol no tiene acceso a esta sección"
                  >
                    <span aria-hidden="true" className={styles.linkIcon}>
                      <Icon name={item.icon} />
                    </span>
                    {item.label}
                  </span>
                ),
              )}
            </div>
          ))}
        </nav>

        <div className={styles.footer}>
          <NavLink className={({ isActive }) => cx(styles.userCard, isActive && styles.active)} to="/perfil">
            <span aria-hidden="true" className={styles.avatar}>
              {initials(user?.name)}
            </span>
            <span className={styles.userMeta}>
              <span className={styles.userName}>{user?.name ?? 'Mi perfil'}</span>
              <span className={styles.userRole}>
                {user === undefined ? '' : (ROLES[user.role] ?? user.role)}
              </span>
            </span>
            <span aria-hidden="true" className={styles.userChevron}>
              <Icon height={16} name="flecha" width={16} />
            </span>
          </NavLink>
          <button
            aria-label="Cerrar sesión"
            className={styles.logout}
            onClick={() => {
              void logout();
            }}
            title="Cerrar sesión"
            type="button"
          >
            <Icon name="salir" />
          </button>
        </div>
      </header>

      <main className={styles.main} id="contenido">
        <Outlet />
      </main>
    </div>
  );
}

/** Iniciales para el avatar; dos letras bastan y evitan desbordes. */
function initials(name: string | undefined): string {
  if (name === undefined || name.trim().length === 0) {
    return '—';
  }
  const partes = name.trim().split(/\s+/);
  const primera = partes[0]?.[0] ?? '';
  const segunda = partes.length > 1 ? (partes.at(-1)?.[0] ?? '') : '';
  return (primera + segunda).toUpperCase();
}
